/**
 * ProtectedRoute Component
 *
 * Protects entire routes based on permissions and features.
 * Redirects to unauthorized page if user lacks required permissions.
 *
 * Usage in React Router:
 * <Route path="/admin" element={
 *   <ProtectedRoute permission="admin.users.view">
 *     <AdminPage />
 *   </ProtectedRoute>
 * } />
 */

import React, { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useFeatures } from '../../contexts/FeatureContext';

interface ProtectedRouteProps {
  /**
   * Single permission required to access route
   */
  permission?: string;

  /**
   * Array of permissions (requires ANY by default, ALL if requireAll is true)
   */
  permissions?: string[];

  /**
   * If true, user must have ALL permissions (AND logic)
   * Default: false (OR logic)
   */
  requireAll?: boolean;

  /**
   * Feature required to access route
   */
  feature?: string;

  /**
   * Roles allowed to access route (alternative to permission check)
   * Checks user.role against this array
   */
  allowedRoles?: string[];

  /**
   * Path to redirect to if not authorized
   * Default: '/unauthorized'
   */
  redirectTo?: string;

  /**
   * Path to redirect to if not authenticated
   * Default: '/login'
   */
  loginPath?: string;

  /**
   * Children (the route component)
   */
  children: ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  permission,
  permissions,
  requireAll = false,
  feature,
  allowedRoles,
  redirectTo = '/unauthorized',
  loginPath = '/login',
  children,
}) => {
  const location = useLocation();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading: permLoading } = usePermissions();
  const { hasFeature, canUseFeature, loading: featureLoading } = useFeatures();

  /**
   * Build unauthorized URL with context
   */
  const buildUnauthorizedUrl = (reason: {
    permission?: string;
    feature?: string;
    role?: string;
  }) => {
    const params = new URLSearchParams();
    params.set('path', location.pathname);

    if (reason.permission) {
      params.set('permission', reason.permission);
    }
    if (reason.feature) {
      params.set('feature', reason.feature);
    }
    if (reason.role) {
      params.set('role', reason.role);
    }

    return `${redirectTo}?${params.toString()}`;
  };

  // Show loading state while authentication/permissions are loading
  if (authLoading || permLoading || featureLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check authentication first
  if (!isAuthenticated || !user) {
    return <Navigate to={loginPath} replace />;
  }

  // Check role-based access (if specified)
  if (allowedRoles && allowedRoles.length > 0) {
    if (!allowedRoles.includes(user.role || '')) {
      const unauthorizedUrl = buildUnauthorizedUrl({
        role: allowedRoles.join(' or '),
      });
      return <Navigate to={unauthorizedUrl} replace />;
    }
  }

  // Check single permission
  if (permission) {
    if (!hasPermission(permission)) {
      const unauthorizedUrl = buildUnauthorizedUrl({ permission });
      return <Navigate to={unauthorizedUrl} replace />;
    }
  }

  // Check multiple permissions
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    if (!hasAccess) {
      const unauthorizedUrl = buildUnauthorizedUrl({
        permission: permissions.join(', '),
      });
      return <Navigate to={unauthorizedUrl} replace />;
    }
  }

  // Check feature access
  if (feature) {
    if (!hasFeature(feature)) {
      const unauthorizedUrl = buildUnauthorizedUrl({ feature });
      return <Navigate to={unauthorizedUrl} replace />;
    }

    // Also check usage limits
    if (!canUseFeature(feature)) {
      const limitExceededUrl = buildUnauthorizedUrl({ feature });
      return <Navigate to={limitExceededUrl} replace />;
    }
  }

  // All checks passed - render children
  return <>{children}</>;
};

/**
 * AdminRoute Component
 *
 * Convenience component for admin-only routes
 * Requires 'admin' or 'super_admin' role
 */
export const AdminRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
      {children}
    </ProtectedRoute>
  );
};

/**
 * SuperAdminRoute Component
 *
 * Convenience component for super admin-only routes
 */
export const SuperAdminRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <ProtectedRoute allowedRoles={['super_admin']}>
      {children}
    </ProtectedRoute>
  );
};
